import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kBrowserSettingsCore } from "../../../Admin/kReusableCoreTS/kBrowserSettingsCore";
import { AppBrowserSettings } from "./AppBrowserSettings";
import { AppThemeRules } from "./AppTheme";

// AppThemeRun - the browser half of the theme choice (`AppTheme` holds the rules and the control's markup).
//   - `Boot` (from `AppMain.Start`, first thing): puts the stored choice on <html> and listens ONCE on the document for a
//     click on any `[data-wac-theme]` button - the footer is repainted with innerHTML on every paint, so a listener on
//     the buttons themselves would be lost with them.
//   - a click: stores the choice, sets <html data-theme> (or removes it for "system"), marks the pressed button, and
//     re-marks the contact frame's <html> when one is open (a later frame is marked before it loads, `AppMain.Frame`).
// ⛔ EVERY `localStorage` CALL IS WRAPPED: the accessor THROWS in private browsing, under a blocked-storage policy and at
// quota - a theme must never blank the app. A refused read is "system"; a refused write only means "not remembered".

export class AppThemeStore {
    /** A storage refusal is the shared class's "nothing stored": the device's theme applies. */
    public static Read = (): string => {
        if (getCoreUtils().KErrorMode === true) { return AppThemeRules.System; }
        try {
            return AppThemeRules.Parse(AppBrowserSettings.Local().ReadText(AppThemeRules.StoreKey).Value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeStore.Read");
            return AppThemeRules.System;
        }
    };

    /** A storage refusal only means the choice is not remembered next visit (the shared class answers it as a plain outcome). */
    public static Write = (Choice: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Value: string = AppThemeRules.Parse(Choice);
            const Settings: kBrowserSettingsCore = AppBrowserSettings.Local();
            if (Value === AppThemeRules.System) { Settings.Remove(AppThemeRules.StoreKey); return; }
            Settings.WriteText(AppThemeRules.StoreKey, Value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeStore.Write");
        }
    };
}

export class AppThemeRun {
    private Listening: boolean = false;

    /** The choice now (what the store says). */
    public Chosen = (): string => {
        if (getCoreUtils().KErrorMode === true) { return AppThemeRules.System; }
        try {
            return AppThemeStore.Read();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeRun.Chosen");
            return AppThemeRules.System;
        }
    };

    public Boot = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Apply(this.Chosen());
            if (this.Listening === true) { return; }
            this.Listening = true;
            document.addEventListener("click", this.OnClick);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeRun.Boot");
        }
    };

    /** The attribute on <html> and on an open contact frame's <html>, and the pressed state of the control. */
    public Apply = (Choice: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Value: string = AppThemeRules.AttrValue(Choice);
            this.Mark(document.documentElement, Value);
            const Frame: HTMLIFrameElement | null = document.getElementById("wac-frame") as HTMLIFrameElement | null;
            if (Frame !== null && Frame.contentDocument !== null) { this.Mark(Frame.contentDocument.documentElement, Value); }
            const Parsed: string = AppThemeRules.Parse(Choice);
            const Buttons: NodeListOf<Element> = document.querySelectorAll("[" + AppThemeRules.PickAttr + "]");
            for (const One of Array.from(Buttons)) {
                let Pressed: string = "false";
                if (nz(One.getAttribute(AppThemeRules.PickAttr)) === Parsed) { Pressed = "true"; }
                // ⛔ THE BAR'S MENU ITEMS ARE `menuitemradio` (state = `aria-checked`); the footer's buttons are toggles (`aria-pressed`).
                if (nz(One.getAttribute("role")) === "menuitemradio") { One.setAttribute("aria-checked", Pressed); One.removeAttribute("aria-pressed"); }
                else { One.setAttribute("aria-pressed", Pressed); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeRun.Apply");
        }
    };

    private Mark = (Root: Element | null, Value: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Root === null) { return; }
            if (Value.length === 0) { Root.removeAttribute(AppThemeRules.Attr); return; }
            Root.setAttribute(AppThemeRules.Attr, Value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeRun.Mark");
        }
    };

    private OnClick = (Evt: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: Element | null = Evt.target as Element | null;
            if (Target === null || typeof Target.closest !== "function") { return; }
            const Button: Element | null = Target.closest("[" + AppThemeRules.PickAttr + "]");
            if (Button === null) { return; }
            const Choice: string = AppThemeRules.Parse(Button.getAttribute(AppThemeRules.PickAttr));
            AppThemeStore.Write(Choice);
            this.Apply(Choice);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThemeRun.OnClick");
        }
    };

    /** ⛔ LAST MEMBER (the static-singleton rule in CLAUDE.md). */
    public static readonly Shared: AppThemeRun = new AppThemeRun();
}
